import type { ReactNode, ElementType } from 'react'
import InfoTooltip from './InfoTooltip'

// Shared card wrapper used by all chart sections in Reports.
// Accepts an icon, title, subtitle, optional info (hover explanation next to title),
// optional badge (top-right), optional footer (legend row), and children (the chart itself).
// `bodyClass` styles the wrapper around the chart body - e.g. pass
// "flex-1 flex flex-col justify-center" to vertically centre a short chart when
// the grid stretches the card to match a taller neighbour. Omitted = no change.
export function ChartCard({ icon: Icon, iconGrad, title, subtitle, info, badge, footer, children, bodyClass }: {
  icon: ElementType; iconGrad: string; title: string; subtitle: string; info?: ReactNode; badge?: ReactNode; footer?: ReactNode; children: ReactNode; bodyClass?: string
}) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm flex flex-col">
      <div className="flex items-center justify-between mb-5 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 bg-gradient-to-br ${iconGrad} rounded-xl flex items-center justify-center`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <h3 className="font-bold text-slate-800">{title}</h3>
              {info && <InfoTooltip>{info}</InfoTooltip>}
            </div>
            <p className="text-xs text-slate-400">{subtitle}</p>
          </div>
        </div>
        {badge}
      </div>
      <div className={bodyClass}>{children}</div>
      {footer && <div className="flex items-center justify-center gap-6 mt-5 pt-4 border-t border-slate-100 flex-shrink-0">{footer}</div>}
    </div>
  )
}

// Small colour dot + label used in chart legend footers.
export const Dot = ({ color, label }: { color: string; label: string }) => (
  <span className="flex items-center gap-2 text-xs text-slate-600"><span className={`w-3 h-3 ${color} rounded-sm`} />{label}</span>
)

// Pill badge used in the top-right corner of ChartCards.
export const Badge = ({ cls, children }: { cls: string; children: ReactNode }) => (
  <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${cls}`}>{children}</span>
)
